/**
 * `#` job tags in notes (JT1-1738).
 *
 * A tag is stored twice: as plain `#Job Title` text in the note, and as the job's id on the comment.
 * These helpers decide where in the text each tagged job's title sits. The mention field uses them
 * to drop tags whose text has been deleted, and the note renderer uses them to turn the text into
 * links, so the two always agree on what counts as a tag.
 */

export type TaggableJob = {
  id: string;
  title: string;
};

export type JobTagRange<T extends TaggableJob> = {
  start: number;
  end: number;
  job: T;
};

/** The text inserted for a job: `#` plus the title, with runs of whitespace collapsed. */
export const formatJobTag = (title: string) => `#${(title ?? '').replace(/\s+/g, ' ').trim()}`;

// A tag must not run on into a word: `#Dev` is not a tag inside `#Developer`.
const continuesWord = (char: string) => /[A-Za-z0-9]/.test(char);

/**
 * Where each job's tag appears in `text`, in text order and never overlapping.
 *
 * Matching is case-insensitive and by plain string search, so a title such as `C++ (Senior)` needs
 * no escaping. Longer tags claim their text first, so `#Dev` never takes the front of `#Dev Lead`.
 * When several tagged jobs share a title, its occurrences go to them in the order the jobs are given;
 * a job whose title appears more than once gets every remaining occurrence.
 */
export function findJobTagRanges<T extends TaggableJob>(text: string, jobs: T[] | undefined): JobTagRange<T>[] {
  if (!text || !jobs?.length) {
    return [];
  }

  const haystack = text.toLowerCase();

  // Jobs grouped by the tag they would appear as, longest tag first.
  const byTag = new Map<string, T[]>();
  jobs.forEach((job) => {
    const tag = formatJobTag(job.title).toLowerCase();
    if (tag.length > 1) {
      byTag.set(tag, [...(byTag.get(tag) ?? []), job]);
    }
  });

  const tags = Array.from(byTag.keys()).sort((a, b) => b.length - a.length);

  const ranges: JobTagRange<T>[] = [];
  const overlaps = (start: number, end: number) => ranges.some((range) => start < range.end && end > range.start);

  tags.forEach((tag) => {
    const sharing = byTag.get(tag) as T[];
    let occurrence = 0;
    let from = 0;

    while (from < haystack.length) {
      const start = haystack.indexOf(tag, from);

      if (start === -1) {
        break;
      }

      const end = start + tag.length;

      if (!overlaps(start, end) && !continuesWord(text.charAt(end))) {
        ranges.push({ start, end, job: sharing[Math.min(occurrence, sharing.length - 1)] });
        occurrence += 1;
        from = end;
      } else {
        from = start + 1;
      }
    }
  });

  return ranges.sort((a, b) => a.start - b.start);
}

/** The jobs whose tag is still somewhere in the text. */
export function keepTaggedJobsInText<T extends TaggableJob>(text: string, jobs: T[] | undefined): T[] {
  if (!jobs?.length) {
    return [];
  }

  const present = new Set(findJobTagRanges(text, jobs).map((range) => range.job.id));

  return jobs.filter((job) => present.has(job.id));
}
